'use client';

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { COLORS, GET_MASTER_OFFICES } from '@/constants';
import { SearchIcon2 } from '@/components';
import { useTranslations } from 'next-intl';
import type { ImpersonatedBroker } from '@/contexts/masterAdmin';
import { filterBrokers } from './utils';
import { MasterAdminSkeleton } from './MasterAdminSkeleton';
import { BrokerList } from './components';
import { ScreenContainer, SearchRow } from './styled';

const PAGE_SIZE = 100;

type OfficeRow = {
  userId: string;
  name?: string | null;
  town?: string | null;
  zip?: string | null;
  adminEmail?: string | null;
};

type OfficePage = {
  numFound: number;
  offices: OfficeRow[];
};

function toBroker(office: OfficeRow): ImpersonatedBroker {
  const place = [office.zip, office.town].filter(Boolean).join(' ');
  const officeName = [office.name, place].filter(Boolean).join(', ');
  return {
    id: office.userId,
    name: office.adminEmail || office.name || office.userId,
    officeName,
  };
}

async function loadOffices(): Promise<ImpersonatedBroker[]> {
  const offices: OfficeRow[] = [];
  let page = 0;
  let numFound = 0;
  do {
    const { data } = await axios.get<OfficePage>(GET_MASTER_OFFICES, {
      params: { currentPageIndex: page, itemsPerPage: PAGE_SIZE },
    });
    numFound = data.numFound;
    const batch = data.offices ?? [];
    offices.push(...batch);
    page += 1;
    if (batch.length === 0) break;
  } while (offices.length < numFound);
  return offices.map(toBroker);
}

export const MasterAdminScreen = () => {
  const t = useTranslations('masterAdmin');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [brokers, setBrokers] = useState<ImpersonatedBroker[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadOffices()
      .then((rows) => {
        if (!cancelled) setBrokers(rows);
      })
      .catch((error) => {
        console.error('[master-admin] office list failed', error);
        if (!cancelled) setBrokers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => filterBrokers(brokers, query), [brokers, query]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setPage(0);
  };

  if (loading) return <MasterAdminSkeleton />;

  return (
    <ScreenContainer>
      <Typography fontSize={30} fontWeight={700} color={COLORS['primary/900']} lineHeight={1}>
        {t('title')}
      </Typography>

      <SearchRow>
        <TextField
          fullWidth
          size="small"
          placeholder={t('searchPlaceholder')}
          value={query}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SearchIcon2 width="20px" height="20px" />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              height: '48px',
              '& fieldset': { borderColor: COLORS['neutral/300'] },
              '& fieldset:hover': { borderColor: COLORS['neutral/300'] },
              '&.Mui-focused fieldset': { borderColor: COLORS['neutral/300'] },
            },
          }}
        />
      </SearchRow>

      <BrokerList brokers={filtered} query={query} page={page} onPageChange={setPage} />
    </ScreenContainer>
  );
};
